import { CheckCircle2, RotateCcw, Trash2, Upload } from 'lucide-react';
import { useCallback, useId, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useS3Upload } from '@/hooks/useS3Upload';
import { cn } from '@/lib/utils';

/** 10 MB — matches DEFAULT_MAX_THUMBNAIL in ResourceUploadField. */
const MAX_BYTES = 10 * 1024 * 1024;

/**
 * Mirrors `templateThumbnailContentTypeEnum` in
 * `features/exercises/types/schemas.ts` (re-exported from
 * `features/assessments/types/schemas.ts`) — the presign endpoint rejects
 * anything outside this set with a 400 before it ever reaches S3. Duplicated
 * as a literal here rather than imported so this component stays agnostic of
 * either feature slice (see module doc below); keep the two lists in sync if
 * the BE enum ever changes.
 */
const ACCEPTED_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
const ACCEPT_ATTR = ACCEPTED_CONTENT_TYPES.join(',');

export interface ThumbnailUploadFieldProps {
  /**
   * Request the presigned PUT. Owner supplies this so one component serves
   * both assessments and exercises without knowing either endpoint.
   *
   * Only ever invoked from this component in response to the user picking a
   * file — never on mount or dialog open. The BE writes `s3ThumbnailKey`
   * onto the row inside this call, before any bytes are uploaded, so calling
   * it speculatively would repoint the row at an object that doesn't exist
   * yet (see OBSERVED-CONTRACT.md §5b).
   */
  declareUpload: (file: File) => Promise<{ uploadUrl: string; key: string }>;
  /** Fired after a successful PUT — the owner invalidates its detail query here
   *  so the preview picks up the fresh presigned URL. */
  onUploaded: () => Promise<void> | void;
  /**
   * Clear the artwork. Owner sends `{ s3ThumbnailKey: null }` and the
   * returned promise settles with that PATCH.
   *
   * This field awaits the promise itself: it derives its own "removing"
   * pending state from the call (no separate `removing` prop needed — see
   * task-3-report.md for why that prop was removed rather than kept), and
   * only resets its internal upload status (filename / success / error) once
   * the promise **resolves**. If it rejects, this field leaves its status
   * untouched so the Remove/Retry affordances stay visible — the row may
   * still be pointing at a phantom key (OBSERVED-CONTRACT.md §5b), and
   * silently clearing that state would hide the one place the admin can
   * retry the clear.
   */
  onRemove: () => Promise<void>;
  previewUrl: string | null;
  hasExistingFile: boolean;
  disabled?: boolean;
  disabledReason?: string;
  label?: string;
  description?: string;
}

/**
 * Admin artwork upload for assessment and exercise templates.
 *
 * Sibling of engagement-resources/ResourceUploadField rather than a
 * generalisation of it: that component also carries 500 MB video-content
 * semantics and resource-scoped query keys, and collapsing the two would make
 * both worse. The duplication is deliberate and recorded.
 */
export function ThumbnailUploadField({
  declareUpload,
  onUploaded,
  onRemove,
  previewUrl,
  hasExistingFile,
  disabled = false,
  disabledReason,
  label = 'Card artwork',
  description = 'Shown on the client portal card. JPG, PNG, WebP or GIF — Max 10 MB. Without artwork the card falls back to a typographic cover.',
}: ThumbnailUploadFieldProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [lastFile, setLastFile] = useState<File | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const { upload, reset, status, progress, error, filename } = useS3Upload({
    declareUpload,
    maxSizeBytes: MAX_BYTES,
    acceptedContentTypes: ACCEPTED_CONTENT_TYPES,
  });

  // Tracks whether a network write "may have happened and has not been
  // confirmed cleaned up since" — i.e. `declareUpload` resolved and the BE
  // transaction actually wrote `s3ThumbnailKey`, and nothing has since
  // proven that key points at a real object. Two other failure modes also
  // land on `status === 'error'` but never touch server state:
  //   - client-side validation in useS3Upload.validate() (oversized file,
  //     disallowed MIME picked via "All Files") — returns before
  //     `declareUpload` is even called.
  //   - `declareUpload` itself rejecting (e.g. the contract's 400 "Standard
  //     templates cannot carry uploaded artwork", or a network error) — the
  //     key write only happens on the 200 path.
  // Only the PUT-reached case can leave a phantom key behind, so only that
  // case should offer Remove when `hasExistingFile` is false, and only that
  // case gets the "may show a broken image" hint below.
  //
  // Deliberately NOT cleared just because a new attempt starts (`status`
  // going back to `'declaring'`): a first attempt can leave a phantom key
  // dangling and then a *second* attempt can fail before ever reaching the
  // network (validation, or declareUpload rejecting) — the first phantom is
  // still there, so the flag must survive that second, no-op-server-side
  // attempt. It only comes back down once something *confirms* the row is
  // clean: a PUT that actually succeeds (the key now points at a real
  // object — see `handleFile`) or a remove that actually succeeds (see
  // `handleRemove`).
  //
  // Derived by adjusting state during render (React's documented pattern for
  // resetting a value in step with another value changing) rather than in a
  // useEffect, so the derived flag is never one render stale and never
  // triggers a separate cascading-render pass.
  const [trackedStatus, setTrackedStatus] = useState(status);
  const [reachedUploadStep, setReachedUploadStep] = useState(false);
  if (status !== trackedStatus) {
    setTrackedStatus(status);
    if (status === 'uploading') {
      setReachedUploadStep(true);
    }
  }

  const handleFile = useCallback(
    async (file: File) => {
      setLastFile(file);
      const key = await upload(file);
      // The BE persisted the key atomically with the presign, so a successful
      // PUT is the whole write — the owner just refetches.
      if (key) {
        // A successful PUT replaced whatever the row pointed at with a real
        // object — there is no phantom key to flag anymore. (hasExistingFile
        // may not have refreshed in the caller's cache yet, but that staleness
        // is the caller's `onUploaded` refetch to resolve, not this flag's
        // job — this flag only tracks *this field's own* unconfirmed writes.)
        setReachedUploadStep(false);
        await onUploaded();
      }
    },
    [upload, onUploaded],
  );

  const handleRemove = useCallback(async () => {
    setIsRemoving(true);
    try {
      await onRemove();
      // Only clear this field's own upload status once the owner's PATCH is
      // confirmed to have landed — see the `onRemove` doc above. The row is
      // now confirmed null, so any earlier unconfirmed-write flag no longer
      // applies either.
      reset();
      setLastFile(null);
      setReachedUploadStep(false);
    } catch {
      // Owner surfaces the failure (e.g. a toast); this field intentionally
      // leaves status/lastFile/reachedUploadStep alone so Remove/Retry stay
      // reachable — the row may still be pointing at a phantom key.
    } finally {
      setIsRemoving(false);
    }
  }, [onRemove, reset]);

  const isBusy = status === 'declaring' || status === 'uploading';
  // The presign call overwrites `s3ThumbnailKey` server-side before any
  // bytes are uploaded (OBSERVED-CONTRACT.md §5b). If the PUT then fails, the
  // row is left pointing at an object that was never written — its presigned
  // preview 403s, and nothing restores the previous artwork automatically.
  // Offering Remove here even when `hasExistingFile` is false gives the
  // admin a one-click way to null that phantom pointer — but only once the
  // network step was actually reached (see `reachedUploadStep` above);
  // otherwise no phantom key exists and there is nothing to remove.
  const canRemove = hasExistingFile || (status === 'error' && reachedUploadStep);
  // Keep the button mounted (and disabled) while a removal is in flight even
  // if `canRemove` itself would have gone false the instant `isRemoving`
  // flipped true — otherwise the "Removing…" label would never be visible.
  const showRemoveButton = (canRemove || isRemoving) && !isBusy;

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={inputId} variant="sentence">
        {label}
      </Label>
      <p className="text-meta-sm text-neutral-500">{description}</p>

      <div
        className={cn(
          'bg-surface flex flex-col gap-3 rounded-md border border-(--color-border) p-4',
          disabled ? 'opacity-60' : null,
        )}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Uploaded artwork preview"
            className="max-h-40 w-auto self-start rounded-md border border-(--color-border) object-contain"
          />
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || isBusy || isRemoving}
          >
            <Upload className="h-4 w-4" aria-hidden /> Choose image
          </Button>

          {filename ? (
            <span className="text-body-sm truncate text-neutral-700">{filename}</span>
          ) : hasExistingFile ? (
            <span className="text-body-sm text-primary-700 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" aria-hidden /> Artwork attached
            </span>
          ) : (
            <span className="text-body-sm text-neutral-500">No image selected</span>
          )}

          {status === 'error' && lastFile ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                reset();
                void handleFile(lastFile);
              }}
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Retry
            </Button>
          ) : null}

          {showRemoveButton ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void handleRemove()}
              disabled={isRemoving || disabled}
              className="text-error-700 hover:text-error-800"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              {isRemoving ? 'Removing…' : 'Remove artwork'}
            </Button>
          ) : null}
        </div>

        <input
          id={inputId}
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          className="hidden"
          data-testid="thumbnail-file-input"
          disabled={disabled || isBusy || isRemoving}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
            // Allow re-picking the same file after a reset.
            e.target.value = '';
          }}
        />

        {isBusy ? (
          <div className="flex flex-col gap-1">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
              <div
                className="bg-primary-500 h-full transition-[width] duration-150 ease-out"
                style={{ width: `${String(progress)}%` }}
              />
            </div>
            <span className="text-meta-sm text-neutral-500">
              {status === 'declaring' ? 'Preparing upload…' : `Uploading… ${String(progress)}%`}
            </span>
          </div>
        ) : null}

        {status === 'success' ? (
          <span className="text-meta-sm text-primary-700">Upload complete.</span>
        ) : null}

        {status === 'error' && error ? (
          <div className="flex flex-col gap-0.5">
            <span className="text-meta-sm text-error-700">{error.message}</span>
            {reachedUploadStep ? (
              <span className="text-meta-sm text-neutral-500">
                The upload didn&apos;t finish — retry, or remove the artwork so the card
                doesn&apos;t show a broken image.
              </span>
            ) : null}
          </div>
        ) : null}

        {disabled && disabledReason ? (
          <span className="text-meta-sm text-neutral-500">{disabledReason}</span>
        ) : null}
      </div>
    </div>
  );
}
